import { Link, useNavigate } from 'react-router-dom';
import type { Activity } from '@/lib/types';
import { activityTypeMeta, getInitials, avatarClass } from '../activityMeta';


interface ActivityCardProps {
  activity: Activity;
  isJoined: boolean;
  onJoin: (id: string) => void;
  onNotify: (msg: string) => void;
}



export default function ActivityCard({ activity, isJoined, onJoin, onNotify }: ActivityCardProps) {
  const meta = activityTypeMeta[activity.type];
  const isHelp = activity.type === 'HELP_REQUEST' || activity.type === 'HELP_OFFER';
  const isFull = activity.maxParticipants !== null && activity.participants >= activity.maxParticipants;
  const displayParticipants = isHelp ? null : activity.participants;

  const navigate = useNavigate();

  const openActivity = () => {
    navigate(`/activite/${activity.id}`);
  };


  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const shareData = {
      title: activity.title,
      url: `https://yonyon.fr/activite/${activity.id}`,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }

      await navigator.clipboard.writeText(shareData.url);
      onNotify('Lien copié dans le presse-papier');
    } catch {
      // Annulation du partage
    }
  }

  return (
    <article
      role="link"
      tabIndex={0}
      onClick={openActivity}
      onKeyDown={(e) => { if (e.key === 'Enter') { openActivity(); } }}
      className="group bg-white border border-background-200 rounded-lg p-[.5rem] md:p-5 hover:border-background-300 transition-colors cursor-pointer"
    >
      {/* Top: icon + content */}
      <div className="flex items-start gap-3 md:gap-4">
        <span className={`w-11 h-11 md:w-12 md:h-12 shrink-0 rounded-lg ${meta.badgeClass} flex items-center justify-center`}>
          <i className={`${meta.icon} text-xl`}></i>
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${meta.badgeClass}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${meta.dotClass}`}></span>
              {meta.label}
            </span>
            <span className="text-xs text-foreground-400">{activity.time}</span>
          </div>

          <Link
            to={`/activite/${activity.id}`}
            className="mt-2 block text-base md:text-lg font-semibold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            {activity.title}
          </Link>

          <p className="mt-1 text-sm text-foreground-500 leading-relaxed line-clamp-2">
            {activity.description}
          </p>
        </div>
      </div>

      {/* Meta row */}
      <div className="mt-3 mb-[0.75rem] flex items-center gap-x-3 gap-y-[0.125rem] md:gap-4 flex-wrap text-xs text-foreground-500">
        <span className="flex items-center gap-1.5">
          <i className="ri-map-pin-line text-foreground-400"></i>
          {activity.location}
        </span>

        {/* Retour à la ligne uniquement sur mobile */}
        <span className="basis-full md:hidden"></span>

        <span className="flex items-center gap-1.5">
          <i className="ri-navigation-line text-foreground-400"></i>
          {activity.distanceKm.toLocaleString('fr-FR')} km
        </span>

        {!isHelp && (
          <span className="flex items-center gap-1.5">
            <i className="ri-team-line text-foreground-400"></i>
            {displayParticipants}
            {activity.maxParticipants != null && `/${activity.maxParticipants}`}
          </span>
        )}

        <span
          className={`flex items-center gap-1.5 ${activity.commentsCount > 0 ? 'text-accent-700 font-semibold' : ''
            }`}
        >
          <i
            className={`ri-chat-3-line ${activity.commentsCount > 0
              ? 'text-accent-500'
              : 'text-foreground-400'
              }`}
          ></i>
          {activity.commentsCount}
        </span>

        {activity.upcomingSessionsCount !== undefined &&
          activity.upcomingSessionsCount > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-md bg-primary-100 px-2.5 py-1.5 text-xs font-semibold text-primary-800">
              <i className="ri-repeat-line text-primary-600"></i>
              {activity.upcomingSessionsCount} séance
              {activity.upcomingSessionsCount > 1 ? 's' : ''}
            </span>
          )}

        <button
          type="button"
          onClick={handleShare}
          onKeyDown={(e) => e.stopPropagation()}
          className="
          flex items-center
          gap-[0.125rem]
          text-[x-small]
          leading-none
          text-foreground-500
          hover:text-primary-600
          transition-colors
          cursor-pointer
          shadow-[0_0_0_1px_currentColor]
          px-[0.15rem]
          py-0
          rounded-[3px]
        "
          aria-label="Partager cette activité"
          title="Partager"
        >
          <i className="ri-share-forward-line text-foreground-400 text-base"></i>
          <span>Partager</span>
        </button>
      </div>

      {/* Tags */}
      {activity.tags.length > 0 && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {activity.tags.map((tag) => (
            <span
              key={tag}
              className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Footer: organizer + action */}
      <div className="mt-4 pt-[.4rem] md:pt-3 border-t border-background-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-8 h-8 rounded-full ${avatarClass(activity.organizer)} flex items-center justify-center text-xs font-bold shrink-0`}>
            {getInitials(activity.organizer)}
          </span>
          <span className="text-xs text-foreground-500 font-medium truncate">
            {activity.organizer}
          </span>
        </div>
        {isHelp ? (
          <Link
            to={`/activite/${activity.id}`}
            className="px-4 py-2 rounded-md border border-primary-300 text-primary-700 text-sm font-semibold hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            {activity.isOwner ? 'Voir la discussion' : activity.type === 'HELP_REQUEST' ? 'Proposer mon aide' : 'Contacter'}
          </Link>
        )
          : isFull && !isJoined ? (
            <button
              type="button"
              disabled
              className="px-4 py-2 rounded-md bg-background-200 text-foreground-400 text-sm font-semibold cursor-not-allowed whitespace-nowrap shrink-0"
            >
              Complet
            </button>
          ) : activity.isOwner ? (
            <div className="px-4 py-2 rounded-md bg-accent-100 text-accent-800 text-sm font-semibold whitespace-nowrap shrink-0">
              <i className="ri-user-star-line mr-1"></i>
              Organisateur
            </div>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onJoin(activity.id) }}
              className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap shrink-0 ${isJoined
                ? 'bg-accent-100 text-accent-800 hover:bg-accent-200'
                : 'bg-primary-500 text-white hover:bg-primary-600'
                }`}
            >
              {isJoined ? 'Inscrit ✓' : 'Rejoindre'}
            </button>
          )}
      </div>
    </article>
  );
}